import { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { fetchAuthSession, getCurrentUser } from "aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import { API_URL } from "../../utils/config";
import { isEiqAdmin } from "../../utils/auth";

const COLUMNS = [
  { id: "username", label: "Username" },
  { id: "sub", label: "Sub" },
  { id: "api_path", label: "API Path" },
  { id: "result", label: "Result" },
  { id: "ip", label: "IP" },
  { id: "status_code", label: "Status Code" },
  { id: "created_ts", label: "Created At" },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const ApiLog = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("created_ts");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const canAccess = await isEiqAdmin();
        if (!canAccess) {
          navigate("/dashboard", { replace: true });
          return;
        }
        setHasAccess(true);
      } catch (error) {
        console.error("Error checking API logs access:", error);
        navigate("/dashboard", { replace: true });
      }
    };

    checkAccess();
  }, [navigate]);

  useEffect(() => {
    if (!hasAccess) {
      return undefined;
    }

    const fetchLogs = async () => {
      setLoading(true);
      try {

        await getCurrentUser();
        const session = await fetchAuthSession();
        if (!session.tokens?.accessToken) {
          throw new Error("No access token available");
        }
        const token = session.tokens.accessToken.toString();
        const res = await fetch(`${API_URL}/api_log/list`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            page: page + 1,
            page_size: rowsPerPage,
          }),
        });
        const data = await res.json();
        const responseData = data.response;
        const list = Array.isArray(responseData?.list)
          ? responseData.list
          : Array.isArray(data.response)
            ? data.response
            : [];

        if (data.status === "1") {
          setRows(list);
          setTotalCount(
            responseData?.total ??
              responseData?.count ??
              data.total_count ??
              data.total ??
              data.count ??
              list.length
          );
        } else {
          console.error("Failed to fetch API logs:", data.message);
          setRows([]);
          setTotalCount(0);
        }
      } catch (error) {
        console.error("Error fetching API logs:", error);
        setRows([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [hasAccess, page, rowsPerPage]);

  const sortedRows = useMemo(
    () => [...rows].sort(getComparator(order, orderBy)),
    [rows, order, orderBy]
  );

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  return (
    <Box className="w-full h-full min-h-[100vh] bg-[#EAF4FC]">
      <Header />
      <div className="w-full pt-10 flex justify-center">
        <div className="w-[92%] bg-white">
          <Box className="px-6 pt-6 flex items-center justify-between gap-4">
            <h2 className="text-xl font-semibold">API Logs</h2>
            <button
              type="button"
              className="rounded border border-[#1976d2] px-4 py-2 text-sm font-medium text-[#1976d2] transition hover:bg-[#1976d2] hover:text-white"
              onClick={() => navigate("/dashboard")}
            >
              Back to Dashboard
            </button>
          </Box>
          <Box className="mt-4 px-6 pb-6">
            <Paper>
              {loading ? (
                <div style={{ textAlign: "center" }}>
                  <CircularProgress color="inherit" style={{ margin: "80px 0" }} />
                </div>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {COLUMNS.map((column) => (
                          <TableCell key={column.id} align="left">
                            <TableSortLabel
                              active={orderBy === column.id}
                              direction={orderBy === column.id ? order : "asc"}
                              onClick={() => handleRequestSort(column.id)}
                            >
                              {column.label}
                            </TableSortLabel>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={COLUMNS.length} align="center">
                            No logs found
                          </TableCell>
                        </TableRow>
                      ) : (
                        sortedRows.map((row) => (
                          <TableRow hover key={row.id}>
                            {COLUMNS.map((column) => (
                              <TableCell key={column.id} align="left">
                                {row[column.id] ?? ""}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                      setRowsPerPage(parseInt(event.target.value, 10));
                      setPage(0);
                    }}
                  />
                </TableContainer>
              )}
            </Paper>
          </Box>
        </div>
      </div>
    </Box>
  );
};

export default ApiLog;
